import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ImmApplicationState, Page, PermitApplication } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['IMMIGRATION_OFFICER', 'SENIOR_IO'];
const SENIOR_ROLES = ['SENIOR_IO'];

const STATES: ImmApplicationState[] = ['SUBMITTED', 'DOCS_VERIFIED', 'OFFICER_REVIEW', 'APPROVED', 'FEE_PAID', 'PERMIT_ISSUED', 'REJECTED', 'WITHDRAWN'];

/** Immigration — the M03 residency/permit-application workflow: verify docs, review, decide, issue. */
export function ImmigrationApplications() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const isSenior = persona ? persona.roles.some((r) => SENIOR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/residency/applications?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<PermitApplication>>(path);

  return (
    <div>
      <PageHeader
        title="Permit Applications"
        subtitle="Residence, permanent, and visitor-extension permit applications (M03)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-52" placeholder="Ref, NIN, passport, or name…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All states</option>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Applicant</th>
                <th className="table-head">Type</th>
                <th className="table-head">Nationality</th>
                <th className="table-head">Submitted</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{a.refNo}</td>
                  <td className="table-cell">
                    {a.fullName}
                    <div className="font-mono text-[11px] text-slate-400">{a.nin ?? a.passportNo}</div>
                  </td>
                  <td className="table-cell">{a.type.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{a.nationality}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(a.submittedAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <ApplicationDetailModal
          id={selectedId}
          isOfficer={isOfficer}
          isSenior={isSenior}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ApplicationDetailModal({
  id,
  isOfficer,
  isSenior,
  onClose,
  onChanged,
}: {
  id: string;
  isOfficer: boolean;
  isSenior: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const appQ = useApiQuery<PermitApplication>(`/api/v1/residency/applications/${id}`);
  const a = appQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [reason, setReason] = useState('');
  const [conditions, setConditions] = useState('');
  const [validYears, setValidYears] = useState('1');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const verifyDocs = () => call(() => api.post(`/api/v1/residency/applications/${id}/verify-docs`));
  const submitReview = () => call(() => api.post(`/api/v1/residency/applications/${id}/submit-review`));
  const withdraw = () => call(() => api.post(`/api/v1/residency/applications/${id}/withdraw`));
  const issue = () => call(() => api.post(`/api/v1/residency/applications/${id}/issue`));
  const decide = () =>
    call(() =>
      api.post(`/api/v1/residency/applications/${id}/decision`, {
        decision: showDecision,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        conditions: conditions
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean),
        validYears: Number.parseInt(validYears, 10) || 1,
      }),
    );

  return (
    <Modal title={a ? `${a.refNo} — ${a.fullName}` : 'Permit application'} onClose={onClose} wide>
      {appQ.error ? <ErrorNote message={appQ.error} /> : null}
      {appQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={a.state} />} />
            <Detail label="Type" value={a.type.replaceAll('_', ' ')} />
            <Detail label="Identity" value={a.nin ?? `PP:${a.passportNo}`} mono />
            <Detail label="Nationality" value={a.nationality} />
            <Detail label="DOB" value={formatDate(a.dob)} />
            <Detail label="Sponsor ref" value={a.sponsorRef ?? '—'} />
          </div>
          <Detail label="Purpose" value={a.purpose} />
          <div>
            <div className="field-label">Documents</div>
            {a.documents.length === 0 ? (
              <p className="text-sm text-slate-400">None attached.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {a.documents.map((d, i) => (
                  <li key={i}>
                    {d.docTypeCode} — <StatusBadge status={d.verified ? 'APPROVED' : 'PENDING'} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {a.rejectionReason ? <ErrorNote message={`Rejected: ${a.rejectionReason}`} /> : null}
          {a.officerNotes ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700">Officer notes: {a.officerNotes}</div>
          ) : null}
          {a.payIntentRefNo ? <Detail label="Fee intent ref" value={a.payIntentRefNo} mono /> : null}

          {isOfficer && a.state === 'SUBMITTED' ? (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              <button className="btn-primary" disabled={busy} onClick={verifyDocs}>
                Verify documents
              </button>
              <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                Withdraw
              </button>
            </div>
          ) : null}

          {isOfficer && a.state === 'DOCS_VERIFIED' ? (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              <button className="btn-primary" disabled={busy} onClick={submitReview}>
                Submit for officer review
              </button>
              <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                Withdraw
              </button>
            </div>
          ) : null}

          {a.state === 'OFFICER_REVIEW' ? (
            isSenior ? (
              <div className="space-y-3 border-t border-slate-100 pt-4">
                {!showDecision ? (
                  <div className="flex flex-wrap gap-2">
                    <button className="btn-success" onClick={() => setShowDecision('APPROVE')}>
                      Approve
                    </button>
                    <button className="btn-danger" onClick={() => setShowDecision('REJECT')}>
                      Reject
                    </button>
                    <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                      Withdraw
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                    <Field label={showDecision === 'APPROVE' ? 'Approval note (optional)' : 'Rejection reason'}>
                      <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    {showDecision === 'APPROVE' ? (
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Conditions (comma separated)">
                          <input className="field-input" value={conditions} onChange={(e) => setConditions(e.target.value)} />
                        </Field>
                        <Field label="Valid years">
                          <input className="field-input" type="number" min="1" max="20" value={validYears} onChange={(e) => setValidYears(e.target.value)} />
                        </Field>
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                        Cancel
                      </button>
                      <button
                        className={showDecision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                        disabled={busy}
                        onClick={decide}
                      >
                        {busy ? 'Submitting…' : `Confirm ${showDecision.toLowerCase()}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <RoleNotice roles={SENIOR_ROLES} />
            )
          ) : null}

          {a.state === 'APPROVED' ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-sm text-sky-800">
              Awaiting the SeyPay fee intent to clear (service-confirmed webhook) before a permit can be issued.
            </div>
          ) : null}

          {a.state === 'FEE_PAID' ? (
            isSenior ? (
              <button className="btn-success" disabled={busy} onClick={issue}>
                {busy ? 'Issuing…' : 'Issue permit'}
              </button>
            ) : (
              <RoleNotice roles={SENIOR_ROLES} />
            )
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Submitted {formatDateTime(a.submittedAt)} · last updated {formatDateTime(a.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
